import { z } from "zod";

/**
 * Brazilian phone number validation regex
 * Matches formats: (00) 00000-0000, (00) 0000-0000, 00000000000, etc.
 */
const phoneRegex = /^\(?[1-9]{2}\)?\s?[9]?[0-9]{4}-?[0-9]{4}$/;

/**
 * Format phone number to (00) 00000-0000 pattern
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");
  
  // Apply formatting based on length
  if (digits.length <= 2) {
    return `(${digits}`;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  } else if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  } else {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
}

/**
 * Schema for public order checkout form
 */
export const publicCheckoutSchema = z.object({
  customerName: z
    .string()
    .trim()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  customerPhone: z
    .string()
    .trim()
    .min(10, "Telefone inválido")
    .max(20, "Telefone inválido")
    .regex(phoneRegex, "Formato de telefone inválido. Use (00) 00000-0000"),
  deliveryAddress: z
    .string()
    .trim()
    .max(500, "Endereço deve ter no máximo 500 caracteres")
    .optional(),
  notes: z
    .string()
    .trim()
    .max(1000, "Observações devem ter no máximo 1000 caracteres")
    .optional(),
});

/**
 * Schema for delivery order validation (requires address)
 */
export const deliveryOrderSchema = publicCheckoutSchema.extend({
  deliveryAddress: z
    .string()
    .trim()
    .min(10, "Endereço deve ter pelo menos 10 caracteres")
    .max(500, "Endereço deve ter no máximo 500 caracteres"),
});

/**
 * Schema for cart items
 */
export const cartItemSchema = z.object({
  id: z.string().uuid("ID do produto inválido"),
  name: z.string().min(1, "Nome do produto é obrigatório"),
  price: z.number().min(0, "Preço inválido"),
  quantity: z.number().int().min(1, "Quantidade deve ser pelo menos 1"),
  notes: z.string().max(500, "Observações muito longas").optional(),
  image_url: z.string().nullable().optional(),
});

/**
 * Schema for complete public order
 */
export const publicOrderSchema = z.object({
  restaurantId: z.string().uuid("ID do restaurante inválido"),
  orderType: z.enum(["delivery", "pickup"], {
    errorMap: () => ({ message: "Tipo de pedido inválido" }),
  }),
  customerName: z
    .string()
    .trim()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  customerPhone: z
    .string()
    .trim()
    .regex(phoneRegex, "Formato de telefone inválido"),
  deliveryAddress: z
    .string()
    .trim()
    .max(500, "Endereço muito longo")
    .optional(),
  deliveryFee: z.number().min(0).optional(),
  notes: z.string().trim().max(1000, "Observações muito longas").optional(),
  items: z.array(cartItemSchema).min(1, "Adicione pelo menos um item ao carrinho"),
});

/**
 * Schema for table order (supports combos)
 */
export const tableOrderSchema = z.object({
  restaurantId: z.string().uuid("ID do restaurante inválido"),
  tableNumber: z.string().min(1, "Número da mesa é obrigatório"),
  items: z.array(cartItemSchema),
  hasComboItems: z.boolean().optional(),
}).refine(
  (data) => data.items.length > 0 || data.hasComboItems === true,
  { message: "Adicione pelo menos um item ao carrinho" }
);

export type PublicCheckoutFormData = z.infer<typeof publicCheckoutSchema>;
export type PublicOrderData = z.infer<typeof publicOrderSchema>;
export type TableOrderData = z.infer<typeof tableOrderSchema>;
export type CartItemData = z.infer<typeof cartItemSchema>;
